import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Tooltip } from "@mui/material";

import "./TruncatedWithTooltip.scss";

const truncateStyle = (lines, style) =>
  lines === 1
    ? {
        display: "block",
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
        ...style,
      }
    : {
        display: "-webkit-box",
        WebkitLineClamp: lines,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        wordBreak: "break-word",
        minWidth: 0,
        ...style,
      };

const mergeRefs = (node, ...refs) => {
  refs.forEach((r) => {
    if (typeof r === "function") r(node);
    else if (r) r.current = node;
  });
};

const TruncatedWithTooltip = ({
  title,
  children,
  lines,
  className,
  style,
  tooltipDir,
}) => {
  const ref = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const check = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    if (lines === 1) {
      setIsTruncated(el.scrollWidth > el.clientWidth + 1);
    } else {
      setIsTruncated(el.scrollHeight > el.clientHeight + 1);
    }
  }, [lines]);

  useLayoutEffect(() => {
    check();
  }, [title, children, lines, check]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [check]);

  const tipRaw = title != null ? String(title).trim() : "";
  const showTip = isTruncated && tipRaw !== "" && tipRaw !== "—";

  const child = React.Children.only(children);
  const mergedStyle = { ...truncateStyle(lines, style), ...child.props.style };
  // The text takes its own direction (`dir="auto"`), so English on an Arabic
  // page reads LTR and, when cut short, keeps its start and ends in "…".
  const mergedClassName = [
    className,
    child.props.className,
    "truncated-with-tooltip",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Tooltip
      title={showTip ? tipRaw : ""}
      disableHoverListener={!showTip}
      componentsProps={{
        tooltip: {
          dir: "auto",
          sx: {
            fontSize: "var(--text-body-sm-font-size, 0.875rem)",
            lineHeight: "var(--text-body-sm-line-height, 1.25rem)",
            maxWidth: 360,
            padding: "var(--space-8, 8px) var(--space-12, 12px)",
            whiteSpace: "normal",
            wordBreak: "break-word",
            ...(tooltipDir ? { direction: tooltipDir } : {}),
          },
        },
      }}
    >
      {React.cloneElement(child, {
        ref: (node) => mergeRefs(node, ref, child.ref),
        className: mergedClassName,
        dir: child.props.dir || "auto",
        style: mergedStyle,
      })}
    </Tooltip>
  );
};

TruncatedWithTooltip.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.element.isRequired,
  lines: PropTypes.number,
  className: PropTypes.string,
  style: PropTypes.object,
  tooltipDir: PropTypes.oneOf(["ltr", "rtl"]),
};

TruncatedWithTooltip.defaultProps = {
  title: undefined,
  lines: 1,
  className: undefined,
  style: undefined,
  tooltipDir: undefined,
};

export default TruncatedWithTooltip;
